import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { requestVipCheck } from "@/components/vip/VipAwardAnnouncer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, MessageSquare, ArrowLeft, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import GiftPicker from "@/components/GiftPicker";
import useAvatarPeek from "@/hooks/useAvatarPeek";
import UserBadge from "@/components/UserBadge";
import ReportBlockControls from "@/components/ReportBlockControls";
import { useChatAttachment, ChatAttachButton } from "@/components/live-room/LiveChatAttach";
import { playDmChime, flashTitle } from "@/hooks/useUnreadDMs";
import ChatMessageBody from "@/components/ChatMessageBody";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function Thread({ convId, other, onBack, onChanged }) {
  const { user } = useAuth();
  const [, setParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const scroller = useRef(null);
  const isNewThread = !convId;
  const peek = useAvatarPeek();
  const attach = useChatAttachment();
  const [flashIds, setFlashIds] = useState(new Set());
  const seen = useRef(null);

  const load = useCallback(async () => {
    if (!convId) { setMessages([]); return; }
    try {
      const { data } = await api.get(`/conversations/${convId}/messages`);
      setMessages(data);
      // new incoming message while the thread is open → chime + flash the bubble + tab title
      if (seen.current) {
        const fresh = data.filter((m) => !seen.current.has(m.id) && m.sender_id !== user?.id);
        if (fresh.length) {
          playDmChime(); flashTitle();
          setFlashIds(new Set(fresh.map((m) => m.id)));
          setTimeout(() => setFlashIds(new Set()), 2500);
        }
      }
      seen.current = new Set(data.map((m) => m.id));
    } catch (e) { console.warn("[inbox] load thread failed:", e); }
  }, [convId, user?.id]);

  useEffect(() => {
    load();
    if (!convId) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load, convId]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages]);

  const att = attach.att;
  const canSend = (text.trim() || att?.status === "ready") && att?.status !== "uploading" && !sending;
  const send = async () => {
    if (!canSend) return;
    setSending(true);
    const body = { to_id: other.id, text: text.trim(), media_url: att?.status === "ready" ? att.url : undefined, media_type: att?.status === "ready" ? att.type : undefined };
    try {
      if (isNewThread) {
        // First message → create the conversation via the generic /messages endpoint,
        // then re-anchor the URL to the new conversation id so replies work.
        const { data } = await api.post("/messages", body);
        setText(""); attach.clear();
        if (data?.conversation_id) {
          setParams({ c: data.conversation_id }, { replace: true });
        }
        onChanged && onChanged();
      } else {
        await api.post(`/conversations/${convId}/messages`, body);
        setText(""); attach.clear();
        await load();
        onChanged && onChanged();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const sendGift = async (giftKey, msg) => {
    if (isNewThread) {
      // Gifts require an existing conversation; nudge the user to send a text first.
      toast.info("Send a message first, then you can gift within the thread.");
      return;
    }
    await api.post(`/conversations/${convId}/gift`, { gift_key: giftKey, message: msg });
    requestVipCheck();
    await load();
    onChanged && onChanged();
  };

  return (
    <div className="flex flex-col h-[calc(100vh-88px)]">
      <div className="flex items-center gap-3 py-3 border-b border-white/5">
        <button onClick={onBack} className="lg:hidden text-zinc-400 hover:text-white">
          <ArrowLeft size={18} />
        </button>
        <UserBadge
          userRef={other?.id || other?.handle}
          href={other?.handle ? `/u/${other.id}` : undefined}
          data-testid="dm-thread-avatar-peek"
          aria-label={`View ${other?.display_name}'s profile`}
          className="rounded-full focus:outline-none focus:ring-2 focus:ring-rose-500/60"
        >
          <Avatar className="w-9 h-9 border border-white/10 hover:border-rose-500 transition-colors">
            <AvatarImage src={other?.avatar_url} />
            <AvatarFallback className="bg-rose-600 text-white text-sm">
              {other?.display_name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </UserBadge>
        <div>
          <UserBadge
            userRef={other?.id || other?.handle}
            href={other?.handle ? `/u/${other.id}` : undefined}
            data-testid="dm-thread-name-peek"
            className="font-medium hover:text-rose-500 text-left"
          >
            {other?.display_name}
          </UserBadge>
        </div>
        {other?.id && (
          <ReportBlockControls targetType="user" targetId={other.id} authorId={other.id} testid="dm-thread" className="ml-auto" iconSize={16} onBlocked={onBack} />
        )}
      </div>
      <div ref={scroller} className="flex-1 overflow-y-auto py-4 space-y-3">
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          const isGift = m.kind === "gift";
          if (isGift) {
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-4 py-3 flex items-center gap-3 border-l-2 ${
                  mine ? "bg-amber-500/15 border-amber-400" : "bg-amber-500/10 border-amber-500"
                }`}>
                  <div className="text-3xl leading-none">{m.gift_emoji || "🎁"}</div>
                  <div className="text-xs">
                    <div className="text-amber-400 uppercase tracking-widest">{m.gift_label || "Gift"}</div>
                    <div className="text-white">{m.gift_coins || 0} coins</div>
                    {m.text && !m.text.startsWith(m.gift_emoji) && (
                      <div className="text-zinc-300 mt-1">{m.text}</div>
                    )}
                    <div className="text-[10px] text-zinc-500 mt-1">{timeAgo(m.created_at)}</div>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div key={m.id} className={`group flex items-end gap-1 ${mine ? "justify-end" : "justify-start"}`}>
              <div data-testid={`dm-bubble-${m.id}`} className={`max-w-[70%] px-3 py-2 text-sm transition-colors duration-700 ${mine ? "bg-rose-600 text-white" : "bg-white/[0.04] text-zinc-100 border-l-2 border-white/10"} ${m.deleted ? "italic text-zinc-500" : ""} ${flashIds.has(m.id) ? "ring-2 ring-amber-300 bg-amber-400/20" : ""}`}>
                {m.media_url && !m.deleted && (
                  m.media_type === "video"
                    ? <video src={m.media_url} controls playsInline className="max-h-72 rounded-sm mb-1 bg-black" data-testid={`dm-media-${m.id}`} />
                    : <a href={m.media_url} target="_blank" rel="noreferrer"><img src={m.media_url} alt="" loading="lazy" className="max-h-72 rounded-sm mb-1 object-contain bg-black/40" data-testid={`dm-media-${m.id}`} /></a>
                )}
                {m.text && <ChatMessageBody text={m.text} />}
                <div className={`text-[10px] mt-1 ${mine ? "text-rose-100/70" : "text-zinc-500"}`}>{timeAgo(m.created_at)}</div>
              </div>
              {!mine && !m.deleted && (
                <ReportBlockControls targetType="message" targetId={m.id} authorId={m.sender_id} showBlock={false} testid={`dm-msg-${m.id}`} className="opacity-60 group-hover:opacity-100" iconSize={12} />
              )}
            </div>
          );
        })}
        {messages.length === 0 && (
          <div className="text-center text-sm text-zinc-500 py-10">
            {isNewThread
              ? <>Send your first message to <span className="text-white">{other?.display_name}</span> 👋</>
              : "Say hi 👋"}
          </div>
        )}
      </div>
      {att && (
        <div className="mt-2 flex items-center gap-3 border border-white/10 bg-black/40 px-3 py-2" data-testid="dm-attachment-chip">
          {att.type === "video" ? <video src={att.localUrl} className="h-14 w-14 object-cover bg-black" muted /> : <img src={att.localUrl} alt="" className="h-14 w-14 object-cover bg-black" />}
          <div className="flex-1 text-xs text-zinc-300">
            {att.status === "uploading" && <>Uploading… {att.progress}%<div className="mt-1 h-1 bg-white/10"><div className="h-1 bg-rose-500 transition-[width]" style={{ width: `${att.progress}%` }} /></div></>}
            {att.status === "ready" && <span className="text-emerald-300">Ready to send</span>}
            {att.status === "error" && <button type="button" onClick={attach.retry} className="text-amber-300 underline" data-testid="dm-attachment-retry">Upload failed — retry</button>}
          </div>
          <button type="button" onClick={attach.clear} aria-label="Remove attachment" data-testid="dm-attachment-remove" className="text-zinc-400 hover:text-white text-lg leading-none">×</button>
        </div>
      )}
      <div className="flex items-center gap-2 pt-3 border-t border-white/5">
        <ChatAttachButton fileRef={attach.fileRef} onPick={attach.pick} onOpen={attach.open} disabled={sending || att?.status === "uploading"} />
        <button type="button" onClick={() => setGiftOpen(true)} data-testid="dm-gift-button" title="Send a gift" aria-label="Send a gift"
          className="shrink-0 h-10 w-10 grid place-items-center rounded-full bg-black/50 hover:bg-black/70 border border-white/15 text-amber-400">
          <Gift size={16} />
        </button>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={att ? "Add a caption (optional)…" : "Say something…"}
          className="bg-black/40 border-white/10 rounded-full h-10 focus-visible:ring-rose-600"
          data-testid="dm-input"
        />
        <Button onClick={send} disabled={!canSend} className="btn-crimson rounded-full h-10 w-10 p-0 shrink-0" data-testid="dm-send" aria-label="Send">
          <Send size={16} />
        </Button>
      </div>
      <GiftPicker
        open={giftOpen}
        onOpenChange={setGiftOpen}
        onSend={sendGift}
        context="dm"
        recipientName={other?.handle}
      />
      {peek.node}
    </div>
  );
}

export default function InboxPage() {
  const [params, setParams] = useSearchParams();
  const activeId = params.get("c");
  const toHandle = params.get("to");
  const [convos, setConvos] = useState([]);
  const [newRecipient, setNewRecipient] = useState(null); // For "start-new-thread" mode via ?to=

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConvos(data);
    } catch (e) {
      console.warn("[inbox] load conversations failed:", e);
      setConvos([]);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  // Handle `?to=<handle>` — resolve to a real conversation if one already
  // exists, or open a fresh "no messages yet" thread with that user as target.
  useEffect(() => {
    if (!toHandle) { setNewRecipient(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/users/${toHandle}`);
        if (cancelled) return;
        // If we already have a conv with them, jump to it. Otherwise arm the
        // "new thread" state and let the user type their first message.
        const existing = convos.find((c) => c.other?.id === data.id);
        if (existing) {
          setParams({ c: existing.id }, { replace: true });
        } else {
          setNewRecipient({
            id: data.id, handle: data.handle,
            display_name: data.display_name, avatar_url: data.avatar_url,
          });
        }
      } catch (e) {
        console.warn("[inbox] resolve recipient failed:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [toHandle, convos, setParams]);

  const active = convos.find((c) => c.id === activeId);
  // Priority: an existing conv from ?c=…, else a fresh recipient from ?to=…
  const showThread = active || newRecipient;

  return (
    <div data-testid="inbox-root" className="p-6 lg:p-8">
      <header className="sticky top-[calc(52px+env(safe-area-inset-top))] lg:top-0 -mx-6 lg:-mx-8 px-6 lg:px-8 py-4 glass border-b border-white/5 mb-4 z-10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageSquare size={20} className="text-rose-500" />
          <h1 className="font-display text-2xl tracking-tight">Inbox</h1>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className={`lg:col-span-2 ${activeId ? "hidden lg:block" : ""}`}>
          {convos.length === 0 && (
            <div className="card-surface p-10 text-center text-sm text-zinc-500">
              Your inbox is empty. Message a member from their profile.
            </div>
          )}
          <div className="space-y-1">
            {convos.map((c) => (
              <button
                key={c.id}
                onClick={() => setParams({ c: c.id })}
                data-testid={`convo-item-${c.id}`}
                className={`w-full text-left card-surface p-3 flex items-center gap-3 hover-lift ${
                  activeId === c.id ? "border-rose-600/50" : ""
                }`}
              >
                <Avatar className="w-10 h-10 border border-white/10 shrink-0">
                  <AvatarImage src={c.other?.avatar_url} />
                  <AvatarFallback className="bg-rose-600 text-white text-sm">
                    {c.other?.display_name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium truncate">{c.other?.display_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">
                      {timeAgo(c.last_message_at)}
                    </div>
                  </div>
                  <div className={`text-xs truncate flex items-center gap-2 ${c.unread_count > 0 ? "text-zinc-200 font-medium" : "text-zinc-500"}`}>
                    <span className="truncate">{c.last_message_preview}</span>
                    {c.unread_count > 0 && (
                      <span data-testid={`convo-unread-${c.id}`} className="ml-auto shrink-0 min-w-[18px] text-center text-[10px] px-1.5 py-0.5 rounded-full bg-rose-600 text-white">
                        {c.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className={`lg:col-span-3 ${!showThread ? "hidden lg:block" : ""}`}>
          {active ? (
            <Thread convId={active.id} other={active.other} onBack={() => setParams({})} onChanged={load} />
          ) : newRecipient ? (
            <Thread
              convId={null}
              other={newRecipient}
              onBack={() => setParams({})}
              onChanged={load}
            />
          ) : (
            <div className="card-surface p-16 text-center text-sm text-zinc-500 h-full flex items-center justify-center">
              Select a conversation to start reading.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
